import { addProtocol } from "maplibre-gl"
import { useMapRasterLayer } from "./useMapRasterLayer"
import { combineLayerStatus } from "./layerStatus"
import { useReportOverlayStatus } from "./useReportOverlayStatus"
import { BASEMAP_RASTER_LAYER_IDS, overlaysBelow } from "./layerOrder"

const CWFIS_WMS = "https://cwfis.cfs.nrcan.gc.ca/geoserver/wms"
const USGS_WMS = "https://dmsdata.cr.usgs.gov/geoserver/"
// Day 1 (today's forecast) occasionally returns an FID-index error -> blank tiles
const USGS_FIREDANGER_LAYER = "wlfp-forecast-1_conus_day_data"

const TILE_PX = 2048 // WMS is high latency, larger tiles are faster

function buildCwfisTileUrl(): string {
    // {bbox-epsg-3857} must remain unencoded — MapLibre substitutes it at request time.
    return (
        `${CWFIS_WMS}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap` +
        `&LAYERS=public:fdr&CRS=EPSG:3857&WIDTH=${TILE_PX}&HEIGHT=${TILE_PX}` +
        `&FORMAT=image/png&TRANSPARENT=true&BBOX={bbox-epsg-3857}`
    )
}

function buildWfpiTileUrl(): string {
    return (
        `${USGS_WMS}firedanger_${USGS_FIREDANGER_LAYER}/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap` +
        `&LAYERS=${USGS_FIREDANGER_LAYER}&CRS=EPSG:3857&WIDTH=${TILE_PX}&HEIGHT=${TILE_PX}` +
        `&FORMAT=image/png&TRANSPARENT=true&BBOX={bbox-epsg-3857}`
    )
}

// QGIS can't parse the `{bbox-epsg-3857}`-templated GetMap URLs above — these discrete
// parts let a server-side QGIS connection string be built separately.
export const FIRE_DANGER_WMS_SOURCES = [
    { layerName: "Fire Danger (Canada)", baseUrl: CWFIS_WMS, wmsLayer: "public:fdr" },
    {
        layerName: "Fire Danger (US)",
        baseUrl: `${USGS_WMS}firedanger_${USGS_FIREDANGER_LAYER}/wms`,
        wmsLayer: USGS_FIREDANGER_LAYER,
    },
]

const USGS_PROTOCOL = "usgs-firedanger"
let usgsProtocolRegistered = false
// USGS stops serving fire-danger tiles once more than one request is in flight -> serial requests only
let usgsQueue: Promise<unknown> = Promise.resolve()

function registerUsgsSerialProtocol(): void {
    if (usgsProtocolRegistered) return
    usgsProtocolRegistered = true
    addProtocol(USGS_PROTOCOL, (params, abortController) => {
        const url = params.url.slice(`${USGS_PROTOCOL}://`.length)
        const request = usgsQueue.then(async () => {
            const response = await fetch(url, { signal: abortController.signal })
            if (!response.ok) throw new Error(`USGS tile request failed: ${response.status}`)
            return { data: await response.arrayBuffer() }
        })
        usgsQueue = request.catch(() => undefined)
        return request
    })
}

const FIRE_DANGER_CA_ABOVE = [...BASEMAP_RASTER_LAYER_IDS, ...overlaysBelow("fire-danger-ca")]
const FIRE_DANGER_US_ABOVE = [...BASEMAP_RASTER_LAYER_IDS, ...overlaysBelow("fire-danger-us")]

export function useFireDangerLayer(enabled: boolean) {
    registerUsgsSerialProtocol()
    const caStatus = useMapRasterLayer(enabled, "fire-danger-ca", buildCwfisTileUrl(), {
        maxzoom: 10,
        opacity: 0.6,
        saturation: -0.6,
        tileSize: TILE_PX,
        above: FIRE_DANGER_CA_ABOVE,
    })
    const usStatus = useMapRasterLayer(
        enabled,
        "fire-danger-us",
        `${USGS_PROTOCOL}://${buildWfpiTileUrl()}`,
        {
            maxzoom: 10,
            opacity: 0.6,
            saturation: -0.4,
            tileSize: TILE_PX,
            above: FIRE_DANGER_US_ABOVE,
        }
    )
    useReportOverlayStatus("fireDanger", combineLayerStatus([caStatus, usStatus]))
}
